/**
 * Frame shape entries for the Figma export showcase.
 *
 * Reuses the proven Resizable* preview wrappers from the shapes showcase
 * (so props always match the real frame components) plus the four frames
 * that render directly. One representative state per frame.
 */

import type { ReactNode } from "react";
import { ConversationFrame } from "@/workshop/components/shapes/conversation-frame/conversation-frame";
import { EmailFrame } from "@/workshop/components/shapes/email-frame/email-frame";
import { NewsletterFrame } from "@/workshop/components/shapes/newsletter-frame/newsletter-frame";
import { LinkedinFrame } from "@/workshop/components/shapes/linkedin-frame/linkedin-frame";
import {
  ResizableText,
  ResizableCode,
  ResizableSearch,
  ResizableTool,
  ResizableWorkflow,
  ResizableImage,
  ResizableMemory,
  ResizableData,
  ResizableHtml,
  ResizableConnection,
  ResizableConnectApp,
} from "../shapes/shapes-page-frames";
import {
  MOCK_TEXT,
  MOCK_CODE,
  MOCK_SEARCH_RESULTS,
  MOCK_TOOL_INPUT,
  MOCK_TOOL_OUTPUT,
  MOCK_IMAGES,
  MOCK_HTML_CHART,
} from "../shapes/shapes-page-data";
import { MOCK_PARALLEL } from "@/workshop/data/mock-workflows";
import type { ExportGroup } from "./export-types";

function PreviewNode({
  children,
  width = 400,
}: {
  children: ReactNode;
  width?: number;
}) {
  return (
    <div className="react-flow__node" style={{ position: "relative", width }}>
      {children}
    </div>
  );
}

export const FRAME_GROUP: ExportGroup = {
  category: "Frames",
  entries: [
    {
      id: "frame-text",
      label: "Text Frame",
      render: () => (
        <ResizableText
          title="Deployment Summary"
          text={MOCK_TEXT}
          status="done"
          agentName="claude-1"
        />
      ),
    },
    {
      id: "frame-code",
      label: "Code Frame",
      render: () => (
        <ResizableCode
          title="useDebounce.ts"
          code={MOCK_CODE}
          language="typescript"
          status="done"
          agentName="claude-1"
        />
      ),
    },
    {
      id: "frame-search",
      label: "Search Frame",
      render: () => (
        <ResizableSearch
          query="react flow custom nodes tutorial"
          results={MOCK_SEARCH_RESULTS}
          status="done"
          agentName="web-agent"
        />
      ),
    },
    {
      id: "frame-tool",
      label: "Tool Frame",
      render: () => (
        <ResizableTool
          title="Database Query"
          toolName="sql_query"
          input={MOCK_TOOL_INPUT}
          output={MOCK_TOOL_OUTPUT}
          status="done"
          agentName="db-agent"
        />
      ),
    },
    {
      id: "frame-workflow",
      label: "Workflow Frame",
      render: () => (
        <ResizableWorkflow
          title="Weekly Report"
          flowSteps={MOCK_PARALLEL.steps}
          status="streaming"
          agentName="deploy-agent"
          onApprove={() => {}}
          onReject={() => {}}
        />
      ),
    },
    {
      id: "frame-image",
      label: "Image Frame",
      render: () => (
        <ResizableImage
          title="Design Mockups"
          images={MOCK_IMAGES}
          status="done"
          agentName="design-agent"
        />
      ),
    },
    {
      id: "frame-memory",
      label: "Memory Frame",
      render: () => (
        <ResizableMemory title="Knowledge Graph" agentName="memory-agent" />
      ),
    },
    {
      id: "frame-data",
      label: "Data Frame",
      render: () => <ResizableData title="Revenue by Region" agentName="Analyst" />,
    },
    {
      id: "frame-html",
      label: "HTML Frame",
      render: () => (
        <ResizableHtml html={MOCK_HTML_CHART} status="done" agentName="agent-1" />
      ),
    },
    {
      id: "frame-connection",
      label: "Connection Frame — Hub",
      render: () => <ResizableConnection agentName="System" />,
    },
    {
      id: "frame-connect-app",
      label: "Connection Frame — Single App",
      render: () => (
        <ResizableConnectApp
          appId="gmail"
          reason="I need to send the campaign email you requested."
          agentName="Email Agent"
        />
      ),
    },
    {
      id: "frame-conversation",
      label: "Conversation Frame",
      render: () => (
        <PreviewNode>
          <ConversationFrame
            title="Research Chat"
            agentName="Assistant"
            isStreaming
          />
        </PreviewNode>
      ),
    },
    {
      id: "frame-email",
      label: "Email Frame",
      render: () => (
        <PreviewNode>
          <EmailFrame
            title="Proposal Draft"
            agentName="Writer"
            to="cto@techcorp.io"
            subject="Partnership Proposal — Q2 2026"
            body={
              "Hi Marcus,\n\nFollowing our conversation at the summit, I'd like to formally propose a collaboration between our teams..."
            }
          />
        </PreviewNode>
      ),
    },
    {
      id: "frame-newsletter",
      label: "Newsletter Frame",
      render: () => (
        <PreviewNode>
          <NewsletterFrame title="Weekly Digest #42" agentName="Newsletter" />
        </PreviewNode>
      ),
    },
    {
      id: "frame-linkedin",
      label: "LinkedIn Frame",
      render: () => (
        <PreviewNode>
          <LinkedinFrame
            title="Product Launch Post"
            agentName="Social"
            postText={
              "Excited to announce Denker v2.0 is live!\n\nAfter months of building, we've shipped the canvas workspace that lets humans and AI agents co-work visually in real time."
            }
            tags={["#ProductLaunch", "#AI", "#Denker"]}
            charCount="312/3000 characters"
          />
        </PreviewNode>
      ),
    },
  ],
};
